import { Injectable, inject } from '@angular/core';
import { State, StateContext, Action } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import {
  ApiAccessorySearchAction,
  ApiAccessorySearchSuccessAction,
  ApiAccessorySearchErrorAction,
  ApiAccessoryFilterActions,
} from './accessory-search.actions';
import { ProductType } from '../../mobilephone/models/mobilephone-search-enums';
import { AccessoryItem, AccessoryResponse } from '@sales/models/accessory.model';
import { AccessoryService } from '../services/accessory.service';
import { AccessoryFiltersResponse } from '../models/accessory-filters.model';
import { NotificationService } from '@core/services/notification.service';

export interface AccessorySearchStateModel {
  searchResults: AccessoryItem[];
  totalCount: number;
  isLoadingAccessorySearchResults: boolean;
  isLoadingAccessoryFilters: boolean;
  isLoadingAccessoryItem: boolean;
  searchTerm: string;
  accessoryFilters: AccessoryFiltersResponse;
  selectedProductTypes: ProductType[];
}

const initialProductSearchState: AccessorySearchStateModel = {
  searchResults: [],
  isLoadingAccessorySearchResults: false,
  isLoadingAccessoryFilters: false,
  isLoadingAccessoryItem: false,
  totalCount: 0,
  searchTerm: '',
  accessoryFilters: null,
  selectedProductTypes: [],
};

enum Messages {
  AccessorySearchError = 'Feil ved søk',
  AccessorySearchFiltersError = 'Feil ved henting av filtere',
}

@State<AccessorySearchStateModel>({
  name: 'accessorySearch',
  defaults: initialProductSearchState,
})
@Injectable()
export class AccessorySearchState {
  private accessoryService = inject(AccessoryService);
  private notificationService = inject(NotificationService);

  @Action(ApiAccessorySearchAction)
  apiAccessorySearchAction({ dispatch, patchState }: StateContext<AccessorySearchStateModel>) {
    patchState({ isLoadingAccessorySearchResults: true });

    return this.accessoryService.getAccessories().pipe(
      tap((response: AccessoryResponse) => dispatch(new ApiAccessorySearchSuccessAction(response))),
      catchError(() => dispatch(new ApiAccessorySearchErrorAction()))
    );
  }

  @Action(ApiAccessorySearchSuccessAction)
  public apiAccessorySearchSuccessAction(
    { patchState }: StateContext<AccessorySearchStateModel>,
    { accessoryResponse }: ApiAccessorySearchSuccessAction
  ) {
    // In situations where 204 "No Content" is returned
    if (!accessoryResponse) return;

    patchState({
      searchResults: accessoryResponse.results,
      totalCount: accessoryResponse.totalCount,
      isLoadingAccessorySearchResults: false,
    });
  }

  @Action(ApiAccessorySearchErrorAction)
  public apiAccessorySearchErrorAction({ patchState }: StateContext<AccessorySearchStateModel>) {
    this.notificationService.error(Messages.AccessorySearchError);

    patchState({ isLoadingAccessorySearchResults: false });
  }

  @Action(ApiAccessoryFilterActions.FetchAll)
  public apiAccessoryFilterFetchAllAction({ dispatch, getState, patchState }: StateContext<AccessorySearchStateModel>) {
    const hasCachedResults = getState().accessoryFilters;
    if (hasCachedResults) return;

    patchState({ accessoryFilters: null, isLoadingAccessoryFilters: true });

    return this.accessoryService.getAccessoryFilters(true, false).pipe(
      tap((response: AccessoryFiltersResponse) => dispatch(new ApiAccessoryFilterActions.FetchAllSuccess(response))),
      catchError(() => dispatch(new ApiAccessoryFilterActions.FetchAllError()))
    );
  }

  @Action(ApiAccessoryFilterActions.FetchAllSuccess)
  public ApiAccessoryFilterFetchAllSuccessAction(
    { patchState }: StateContext<AccessorySearchStateModel>,
    { accessoryFilters }: ApiAccessoryFilterActions.FetchAllSuccess
  ) {
    patchState({
      accessoryFilters,
      isLoadingAccessoryFilters: false,
    });
  }

  @Action(ApiAccessoryFilterActions.FetchAllError)
  public ApiAccessoryFilterFetchAllErrorAction({ patchState }: StateContext<AccessorySearchStateModel>) {
    this.notificationService.error(Messages.AccessorySearchFiltersError);

    patchState({ isLoadingAccessoryFilters: false });
  }
}
